import { browserInstallID } from "./browser-install-id.ts";

export const DEFAULT_API_BASE_URL = "https://stoptrading-449070741956.asia-southeast1.run.app";

const ALLOWED_API_BASE_URLS = new Set([
  DEFAULT_API_BASE_URL,
  "https://stoptrading-dev-449070741956.asia-southeast1.run.app",
  "http://127.0.0.1:8000",
  "http://localhost:8000",
]);

export function resolveAPIBaseURL(candidate?: string): string {
  const raw = (candidate || DEFAULT_API_BASE_URL).trim().replace(/\/$/, "");
  try {
    const url = new URL(raw);
    const normalized = url.toString().replace(/\/$/, "");
    if (
      url.username
      || url.password
      || url.search
      || url.hash
      || url.pathname !== "/"
      || !ALLOWED_API_BASE_URLS.has(normalized)
    ) return DEFAULT_API_BASE_URL;
    return normalized;
  } catch {
    return DEFAULT_API_BASE_URL;
  }
}

const buildEnvironment = (import.meta as ImportMeta & {
  env?: Record<string, string | undefined>;
}).env;

export const API_BASE_URL = resolveAPIBaseURL(
  buildEnvironment?.VITE_API_BASE_URL,
);

// Some browsers require Window.fetch to be invoked with the Window/Global
// receiver. Passing the bare native function into a transport and later
// calling it as an object property can otherwise throw "Illegal invocation".
export const browserSafeFetch: typeof fetch = (input, init) => {
  const inheritedHeaders = init?.headers
    || (typeof Request !== "undefined" && input instanceof Request ? input.headers : undefined);
  const headers = new Headers(inheritedHeaders);
  const rawURL = typeof input === "string"
    ? input
    : input instanceof URL
      ? input.toString()
      : input.url;
  const isAPIRequest = new URL(rawURL, `${API_BASE_URL}/`).origin
    === new URL(`${API_BASE_URL}/`).origin;
  if (isAPIRequest && !headers.has("X-Client-ID")) {
    headers.set("X-Client-ID", browserInstallID());
  }
  return globalThis.fetch(input, { ...init, headers });
};

/**
 * Public share tokens are bearer credentials and viewing/reporting is
 * intentionally anonymous. This transport rejects owner credentials and
 * bypasses the install-ID injection used by authenticated/product APIs.
 */
export const browserPublicShareFetch: typeof fetch = (input, init) => {
  const rawURL = typeof input === "string"
    ? input
    : input instanceof URL
      ? input.toString()
      : input.url;
  const url = new URL(rawURL, `${API_BASE_URL}/`);
  const api = new URL(`${API_BASE_URL}/`);
  const publicPaths = new Set([
    "/api/v2/shareable-results/public",
    "/api/v2/shareable-results/public/attributions",
    "/api/v2/shareable-results/public/reports",
  ]);
  if (
    url.origin !== api.origin
    || !publicPaths.has(url.pathname)
  ) {
    throw new TypeError("public_share_transport_rejected");
  }
  const inheritedHeaders = init?.headers
    || (typeof Request !== "undefined" && input instanceof Request ? input.headers : undefined);
  const headers = new Headers(inheritedHeaders);
  headers.delete("Authorization");
  headers.delete("X-Client-ID");
  return globalThis.fetch(input, { ...init, headers });
};
